import { Elements, RecurlyProvider } from '@recurly/react-recurly';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { getEnv } from '../Context/env';
import { PaymentMethodTypes } from '../PaymentCaptureMethod/constants';
import usePaymentMethods from '../PaymentOrchestrator/hooks/usePaymentMethods';
import { ApplePayOption, GooglePayOption } from '../PaymentMethodChange/index.styles';
import ApplePay from '../PaymentCaptureMethod/PaymentMethodEntry/ApplePay';
import { PaymentDivider } from '../PaymentCaptureMethod/PaymentMethodEntry/index.styles';
import GooglePay from '../PaymentCaptureMethod/PaymentMethodEntry/GooglePay';
import { Accordion, FlexibleAccordionItem, useAccordion } from '@nebula/accordion';
import PaymentCardForm from '../PaymentCaptureMethod/PaymentCardForm';
import { useDispatch, useSelector } from 'react-redux';
import { InfoIcon } from '@nebula/icons/ui';
import { trackPaymentMethodChangeError } from '../../helpers/switch/analytics';
import { PaymentContext } from '../PaymentOrchestrator/PaymentContextProvider';
import { PAYMENT_ERROR_GROUPS, PAYMENT_ERROR_TYPES } from '../PaymentOrchestrator/constants/paymentErrors';
import paymentMethodChangeTransition from '../PaymentOrchestrator/transitions/paymentMethodChangeTransition';
import useExistingPaymentMethod from '../PaymentCaptureMethod/hooks/useExistingPaymentMethod';
import {
  AccordionHeader,
  SaveCardDetailsButton,
  PaybyCreditDebitLabel,
  StyledCardPaymentContainer,
  CardValidationErrorLabel,
  ValidationErrorContainer,
} from './index.styles';
import CreditCardIcon from '../Icons/credit-card-24.svg';
import { splitsList } from '../../constants';
import useSplits from '../../hooks/useSplits';
import { ThemeContext } from 'styled-components';
import useTrackUpsellStage from '../PaymentOrchestrator/hooks/useTrackUpsellStage';
import { PAYMENT_LOG_STEPS } from '../../constants/paymentLogSteps';
import { trackPaymentMethodTokenizationResult } from '../../helpers/changePaymentMethodNewJourney/analytics';

const PaymentMethodChangeV2: React.FC = () => {
  const { REACT_APP_RECURLY_PUBLIC_KEY: key } = getEnv() as { REACT_APP_RECURLY_PUBLIC_KEY: string };
  const availablePaymentMethods: string[] = usePaymentMethods();
  const theme = useContext(ThemeContext);
  const showApplePay = availablePaymentMethods.includes(PaymentMethodTypes.ApplePay);
  const showGooglePay = availablePaymentMethods.includes(PaymentMethodTypes.GooglePay);
  const { currentOpenItem, updateOpenItem } = useAccordion();
  const formRef = useRef(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const validationErrorRef = useRef<HTMLDivElement>(null);
  const customer = useSelector(state => state.auth?.currentUser);
  const address = customer?.address?.[0];
  const [isFormValid, setIsFormValid] = useState(false);
  const [highlightedField, setHighlightedField] = useState(false);
  const paymentContext = useContext(PaymentContext);
  const { raisePaymentError, clearPaymentErrors } = paymentContext;
  const handleFormValidityChange = (isValid: boolean) => setIsFormValid(isValid);
  const [processingMethodUpdate, setProcessingMethodUpdate] = useState(false);
  const dispatch = useDispatch();
  const { existingPaymentType } = useExistingPaymentMethod();
  const trackUpsellStage = useTrackUpsellStage(true);
  const splits = useSplits([splitsList.webNewChangePaymentMethod]);
  const webNewChangePaymentMethodSplit: boolean = splits[splitsList.webNewChangePaymentMethod];

  // Focus on the Save button and hide validation error when the form becomes valid
  useEffect(() => {
    if (isFormValid) {
      setHighlightedField(false);
      if (saveButtonRef.current) {
        saveButtonRef.current.focus();
      }
    }
  }, [isFormValid]);

  // Clear highlighted fields when accordion is closed
  useEffect(() => {
    if (!currentOpenItem.includes('credit-debit-card')) {
      setHighlightedField(false);
    }
  }, [currentOpenItem]);

  // Focus validation error for accessibility when it appears
  useEffect(() => {
    if (highlightedField && validationErrorRef.current) {
      validationErrorRef.current.focus();
    }
  }, [highlightedField]);

  const handleFailure = (error: any, newPaymentType?: string) => {
    trackPaymentMethodTokenizationResult(false, newPaymentType ?? 'unknown');
    trackPaymentMethodChangeError(error.message);
    trackUpsellStage(PAYMENT_LOG_STEPS.PAYMENT_METHOD_TOKENIZATION_FAILED, {
      message: error?.message,
      code: error?.code,
      name: error?.name,
      statusCode: error?.statusCode,
      paymentMethod: newPaymentType ?? existingPaymentType,
    } as any);

    raisePaymentError({
      message: error.message,
      group: PAYMENT_ERROR_GROUPS.METHOD_CHANGE,
      type: PAYMENT_ERROR_TYPES.METHOD_CHANGE.UNSUCCESSFUL,
    });
    setProcessingMethodUpdate(false);
  };

  const handleSuccess = async (paymentMethodToken: { id: string }, newPaymentType: any) => {
    trackPaymentMethodTokenizationResult(true, newPaymentType);
    const runBeforeTransition = () => setProcessingMethodUpdate(false);

    const updatedPaymentMethodType =
      Object.values(PaymentMethodTypes).find(paymentType => newPaymentType.includes(paymentType)) ?? 'unknown';

    const methodChangeAnalytics = `${existingPaymentType}->${updatedPaymentMethodType}`.toLowerCase();

    await paymentMethodChangeTransition(
      paymentContext,
      paymentMethodToken.id,
      methodChangeAnalytics,
      runBeforeTransition
    )(dispatch);
  };

  const handleSubmit = () => {
    clearPaymentErrors();
    setProcessingMethodUpdate(true);
  };

  // Callback for PaymentCardForm to update highlightedField
  const handleHighlightedFieldChange = (value: boolean) => {
    setHighlightedField(value);
  };
  const handleProcessingMethodUpdate = (value: boolean) => {
    setProcessingMethodUpdate(value);
  };
  return (
    <RecurlyProvider publicKey={key}>
      <Elements>
        {showApplePay && (
          <ApplePayOption disabled={false} $needFullWidth={webNewChangePaymentMethodSplit}>
            <ApplePay isMethodChange onSuccess={handleSuccess} onError={handleFailure} />
            <PaymentDivider>or</PaymentDivider>
          </ApplePayOption>
        )}
        {showGooglePay && (
          <GooglePayOption disabled={false} $needFullWidth={webNewChangePaymentMethodSplit}>
            <GooglePay isMethodChange onSuccess={handleSuccess} onError={handleFailure} />
            <PaymentDivider>or</PaymentDivider>
          </GooglePayOption>
        )}
        {highlightedField && currentOpenItem.includes('credit-debit-card') && (
          <ValidationErrorContainer ref={validationErrorRef} tabIndex={-1} role="alert">
            <InfoIcon iconColor={theme.color.messaging.red[60]} iconSize="xSmall" />
            <CardValidationErrorLabel>Enter new card details to continue</CardValidationErrorLabel>
          </ValidationErrorContainer>
        )}
        <StyledCardPaymentContainer>
          <Accordion>
            <FlexibleAccordionItem
              header={
                <AccordionHeader>
                  <img src={CreditCardIcon} alt="credit card icon" />
                  <PaybyCreditDebitLabel>Pay by credit or debit card</PaybyCreditDebitLabel>
                </AccordionHeader>
              }
              id="credit-debit-card"
              clickHandler={() => updateOpenItem('credit-debit-card')}
              isOpen={currentOpenItem.includes('credit-debit-card')}
              chevronColor="black"
            >
              <>
                <PaymentCardForm
                  innerRef={formRef}
                  customer={customer}
                  address={address}
                  onSubmitAttempt={handleSubmit}
                  onSuccess={handleSuccess}
                  onFailure={handleFailure}
                  onFormValidityChange={handleFormValidityChange}
                  isMethodChange
                  highlightedField={highlightedField}
                  setHighlightedField={handleHighlightedFieldChange}
                  setProcessingMethodUpdate={handleProcessingMethodUpdate}
                />
                <SaveCardDetailsButton
                  ref={saveButtonRef}
                  data-automation-test-element="confirmation-method-change-button"
                  buttonSize="small"
                  label="Save card details"
                  buttonKind="primaryMagenta"
                  type="submit"
                  form="recurly-payment-form"
                  loading={processingMethodUpdate}
                />
              </>
            </FlexibleAccordionItem>
          </Accordion>
        </StyledCardPaymentContainer>
      </Elements>
    </RecurlyProvider>
  );
};

export default PaymentMethodChangeV2;
